import get from 'lodash/get';
import { useEffect } from 'react';
import { Trans } from 'react-i18next';
import { useTheme } from 'styled-components';

import { MedicineSimple } from '@dreryk/drerykweb-medicine-api';

import { FormLabel, StyledFlex, Text } from '@dreryk/react-components';

import { PreciseDosageUnit } from '~libs.shared.prescription';

import { prescriptionTranslationKeys } from '../../../../../translations';
import { DraftPrescriptionsFormController } from '../../draftPrescriptions/DraftPrescriptionsForm.types';
import { createDraftStandardPrescriptionsFieldNameFactory } from '../../draftPrescriptions/standardPrescription/DraftStandardPrescriptionField.utils';

import { DosageUnitsSelect } from './dosageUnits/DosageUnitsSelect';
import { FrequencyNumberInput } from './FrequencyNumberInput';
import { StyledNumericalDosageMedicineUnit } from './NumericalDosageMedicineUnit.styled';
import { QuantityNumberInput } from './QuantityNumberInput';

type NumericalDosageProps = Pick<
   DraftPrescriptionsFormController,
   'register' | 'trigger' | 'formState' | 'setValue' | 'setError' | 'watch'
> & {
   readonly index: number;
   readonly required: boolean;
   readonly dosageFrequencyNotAllowed: boolean;
   readonly initialNumericalDosageFrequency: number | null;
   readonly initialNumericalDosageQuantity: number | null;
   readonly initialDosageUnit: PreciseDosageUnit | null;
   readonly medicine: MedicineSimple;
   readonly labelAction?: JSX.Element;
};

export const NumericalDosage = (props: NumericalDosageProps): JSX.Element => {
   const theme = useTheme();
   const createFieldName = createDraftStandardPrescriptionsFieldNameFactory(props.index);
   const frequencyFieldName = createFieldName('numericalDosageFrequency');
   const quantityFieldName = createFieldName('numericalDosageQuantity');
   const dosageUnitFieldName = createFieldName('dosageUnit');

   const currentFrequency = props.watch(frequencyFieldName);
   const currentQuantity = props.watch(quantityFieldName);

   useEffect(() => {
      if (!currentQuantity) {
         props.trigger(frequencyFieldName);
      }
      if (!currentFrequency) {
         props.trigger(quantityFieldName);
      }
      if (!currentQuantity && !currentFrequency && get(props.formState.errors, dosageUnitFieldName)) {
         props.trigger(dosageUnitFieldName);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentQuantity, currentFrequency]);

   const label = (
      <Trans
         i18nKey={
            props.required
               ? prescriptionTranslationKeys.dosage.dosage
               : prescriptionTranslationKeys.dosage.dosageOptional
         }
         components={{ optionalText: <Text testId={'numericalDosageOptional'} color={theme.placeholder} /> }}
      />
   );

   return (
      <StyledFlex direction={'column'} gap={'2px'}>
         <StyledFlex direction={'row'} alignItems={'center'} gap={'8px'}>
            <FormLabel label={label} testId={'numericalDosage'} compact nowrap />
            {props.labelAction}
         </StyledFlex>
         <StyledFlex gap={'4px'} alignItems={'flex-start'}>
            <FrequencyNumberInput
               register={props.register}
               trigger={props.trigger}
               name={frequencyFieldName}
               defaultValue={props.initialNumericalDosageFrequency}
               required={props.required || Boolean(currentQuantity)}
               notAllowed={props.dosageFrequencyNotAllowed}
            />
            <StyledNumericalDosageMedicineUnit>{'x'}</StyledNumericalDosageMedicineUnit>
            <QuantityNumberInput
               register={props.register}
               name={quantityFieldName}
               defaultValue={props.initialNumericalDosageQuantity}
               required={props.required || Boolean(currentFrequency)}
            />
            <DosageUnitsSelect
               name={dosageUnitFieldName}
               defaultValue={props.initialDosageUnit}
               register={props.register}
               setValue={props.setValue}
               medicine={props.medicine}
               required={props.required || Boolean(currentQuantity || currentFrequency)}
            />
         </StyledFlex>
      </StyledFlex>
   );
};
